import React, { useRef, useState, useEffect, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

const G = "#C88A24";
const mediaUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Flipbook({ pages = [], initialPage = 0, onPageChange, ratio = 1.414 }) {
    const bookRef = useRef(null);
    const wrapRef = useRef(null);
    const [current, setCurrent] = useState(initialPage || 0);
    const [dims, setDims] = useState({ w: 380, h: 538 });
    const jumpedRef = useRef(false);
    const total = pages.length;

    useEffect(() => {
        const calc = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            let h = Math.min(vh - 180, 760);
            let w = h / ratio;
            const maxW = Math.min(vw - 24, 560);
            if (w > maxW) { w = maxW; h = w * ratio; }
            setDims({ w: Math.round(w), h: Math.round(h) });
        };
        calc();
        window.addEventListener("resize", calc);
        return () => window.removeEventListener("resize", calc);
    }, [ratio]);

    const flipNext = useCallback(() => bookRef.current?.pageFlip()?.flipNext(), []);
    const flipPrev = useCallback(() => bookRef.current?.pageFlip()?.flipPrev(), []);
    const goToStart = useCallback(() => bookRef.current?.pageFlip()?.turnToPage(0), []);
    const readyRef = useRef(false);

    const handleInit = useCallback(() => {
        if (jumpedRef.current) return;
        jumpedRef.current = true;
        const target = Math.min(Math.max(initialPage || 0, 0), total - 1);
        if (target > 0) {
            setTimeout(() => {
                bookRef.current?.pageFlip()?.turnToPage(target);
                setTimeout(() => { readyRef.current = true; }, 250);
            }, 60);
        } else {
            readyRef.current = true;
        }
    }, [initialPage, total]);

    const handleFlip = useCallback((e) => {
        setCurrent(e.data);
        if (readyRef.current) onPageChange?.(e.data);
    }, [onPageChange]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "ArrowRight") flipNext(); if (e.key === "ArrowLeft") flipPrev(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [flipNext, flipPrev]);

    if (!total) return null;

    return (
        <div className="w-full flex flex-col items-center" ref={wrapRef} data-testid="flipbook">
            <div style={{ perspective: 2000, width: dims.w }} className="mx-auto flex justify-center select-none">
                <HTMLFlipBook
                    key={`${dims.w}x${dims.h}`}
                    width={dims.w}
                    height={dims.h}
                    size="fixed"
                    minWidth={dims.w}
                    maxWidth={dims.w}
                    minHeight={dims.h}
                    maxHeight={dims.h}
                    drawShadow
                    maxShadowOpacity={0.5}
                    flippingTime={700}
                    usePortrait
                    mobileScrollSupport
                    clickEventForward
                    useMouseEvents
                    ref={bookRef}
                    onInit={handleInit}
                    onFlip={handleFlip}
                    className="mx-auto"
                >
                    {pages.map((src, i) => (
                        <div key={i} className="bg-white overflow-hidden flex items-center justify-center" data-density={i === 0 ? "hard" : "soft"}>
                            <img src={mediaUrl(src)} alt={`Page ${i + 1}`} loading={i < 3 ? "eager" : "lazy"} className="w-full h-full object-contain pointer-events-none" draggable={false} />
                        </div>
                    ))}
                </HTMLFlipBook>
            </div>

            <div className="mt-5 flex items-center gap-4">
                <button onClick={flipPrev} disabled={current <= 0} data-testid="flip-prev" aria-label="Previous page"
                    className="h-11 w-11 rounded-full grid place-items-center border transition-colors disabled:opacity-30" style={{ borderColor: `${G}66`, color: G }}>
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-xs tracking-[0.25em]" style={{ color: G }} data-testid="flip-indicator">
                    {Math.min(current + 1, total)} / {total}
                </span>
                <button onClick={flipNext} disabled={current >= total - 1} data-testid="flip-next" aria-label="Next page"
                    className="h-11 w-11 rounded-full grid place-items-center border transition-colors disabled:opacity-30" style={{ borderColor: `${G}66`, color: G }}>
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>
            <p className="mt-2 text-[10px] tracking-widest text-white/30">SWIPE OR DRAG THE CORNER TO TURN THE PAGE</p>
            {current > 0 && (
                <button onClick={goToStart} data-testid="flip-restart" className="mt-2 text-[11px] tracking-widest underline underline-offset-4 transition-opacity hover:opacity-80" style={{ color: G }}>
                    JUMP TO START
                </button>
            )}
        </div>
    );
}
